import type { ThemeColorPreset } from '@sites/site-theme/src/types/theme-color.type';
import type { ThemeFont, ThemeFontPreset } from '@sites/site-theme/src/types/theme-font.type';
import type { ThemeButtonPreset } from '@sites/site-theme/src/types/theme-button.type';

import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TranslateModule } from '@ngx-translate/core';

import { SpTransparentIconComponent } from '@ui/sp-transparent-icon/src/sp-transparent-icon.component';

import { FONT_FAMILIES } from '@sites/site-theme/src/config/fonts.const';
import { ThemeFontTags } from '@sites/site-theme/src/enums/font-tags.enum';
import { ThemeSettings } from '@sites/site-theme/src/enums/theme-settings.enum';
import { formatThemeFontProperty } from '@sites/site-theme/src/helpers/font-css';
import { ThemeStateService } from '@sites/site-theme/src/state';

import { SpThemeButtonPresetComponent } from './button-preset/button-preset.component';

/** One font as the strip prints it: the family's display name, its CSS stack, and its size. */
type FontPreview = { name: string; family: string; size: string };

/** The two tags a font preset is previewed by. */
type FontPair = { h1: FontPreview; p: FontPreview };

const EMPTY_PREVIEW: FontPreview = { name: '', family: '', size: '' };

function toPreview(font: ThemeFont | undefined): FontPreview {
    if (!font) {
        return EMPTY_PREVIEW;
    }

    return {
        name: FONT_FAMILIES[font.font].name,
        family: formatThemeFontProperty(font, 'fontFamily', 'desktop') ?? '',
        size: formatThemeFontProperty(font, 'fontSize', 'desktop') ?? '',
    };
}

function toFontPair(fonts: readonly ThemeFont[]): FontPair {
    return {
        h1: toPreview(fonts.find((font) => font.tag === ThemeFontTags.h1)),
        p: toPreview(fonts.find((font) => font.tag === ThemeFontTags.p)),
    };
}

@Component({
    selector: 'sp-theme-preset-settings',
    templateUrl: './preset-settings.component.html',
    styleUrls: ['./preset-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BsDropdownModule, SpTransparentIconComponent, SpThemeButtonPresetComponent, TranslateModule],
})
export class SpThemePresetSettingsComponent {
    private readonly themeState = inject(ThemeStateService);

    public readonly ThemeSettings = ThemeSettings;

    public readonly selectedThemeColors = this.themeState.selectedColors;
    public readonly selectedThemeColorIndex = this.themeState.selectedColorIndex;
    public readonly selectedThemeButtons = this.themeState.selectedButtons;
    public readonly selectedThemeButtonIndex = this.themeState.selectedButtonIndex;
    public readonly selectedThemeFontIndex = this.themeState.selectedFontIndex;
    public readonly colorsPresets = this.themeState.colorPresets;
    public readonly buttonsPresets = this.themeState.buttonPresets;

    /** What the closed font dropdown shows: the h1 and p of the theme's current selection. */
    public readonly currentFonts = computed<FontPair>(() => toFontPair(this.themeState.selectedFonts()));

    /**
     * Each shipped font preset, flattened to what the strip prints.
     *
     * The template used to call `findTitleFont(preset)`, `fontFamilyOf(font)` and `fontSizeOf(font)`
     * per row, per change-detection pass — which is why the component carried two `Map` caches and
     * an `ngOnDestroy` to clear them. A `computed` re-runs only when the presets actually change, so
     * the caches, the lookups and the lifecycle hook all go with it.
     */
    public readonly fontPresetPreviews = computed(() =>
        this.themeState.fontPresets().map((preset: ThemeFontPreset) => ({
            preset,
            index: preset.index,
            fonts: toFontPair(preset.values),
        }))
    );

    // Re-picking the row already selected is not an edit: it would only add an undo step and a save.
    public selectColorsThemeHandler(colors: ThemeColorPreset): void {
        if (colors.index === this.selectedThemeColorIndex()) {
            return;
        }

        this.themeState.changeColors({ selected: colors.values, selectedIndex: colors.index });
    }

    public selectButtonsThemeHandler(buttons: ThemeButtonPreset): void {
        if (buttons.index === this.selectedThemeButtonIndex()) {
            return;
        }

        this.themeState.changeButtons({ selected: buttons.values, selectedIndex: buttons.index });
    }

    public selectFontsThemeHandler(fonts: ThemeFontPreset): void {
        if (fonts.index === this.selectedThemeFontIndex()) {
            return;
        }

        this.themeState.changeFonts({ selected: fonts.values, selectedIndex: fonts.index, syncWithDesktop: true });
    }

    public openDetailSettingsView(setting: ThemeSettings): void {
        this.themeState.setActiveSetting(setting);
    }
}
